import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { OpenTokContext } from '../../../../contexts/opentok/opentok-context';
import { AVContext } from '../../../../contexts/audio-video';
import Icon, { COLORS, ICONS } from '../../../general-components/icon';
import { GreenroomState as GRS } from '../../../../stores/types/types';
import { ConnectedUser } from '../../../../stores/types/connection';
import SelectSmall from '../../../general-components/select-small';
import { SetMediaRegionForRoom } from '../../../../connection/video';
import * as Alerts from '../../../general-components/alert-service';
import { ROOM_STATES } from '../../../../stores/reducers/settings';

const VONAGE_REGIONS = [
	{ label: 'US East (Dulles)',          value: 'us-east' },
	{ label: 'US West (Portland)',        value: 'us-west' },
	{ label: 'Canada (Winnipeg)',         value: 'ca-central' },
	{ label: 'South America (São Paulo)', value: 'sa-east' },
	{ label: 'Europe (Dublin)',           value: 'eu' },
	{ label: 'Europe (Frankfurt)',        value: 'eu-de' },
	{ label: 'Asia Pacific (Tokyo)',      value: 'ap-northeast-1' },
	{ label: 'Asia Pacific (Seoul)',      value: 'ap-northeast-2' },
	{ label: 'Asia Pacific (Mumbai)',     value: 'ap-south-1' },
	{ label: 'Asia Pacific (Singapore)', value: 'ap-southeast-1' },
	{ label: 'Asia Pacific (Sydney)',     value: 'ap-southeast-2' },
];

const REGION_PLACEHOLDER = { label: 'Select region...', value: '' };

interface DisplayRow {
	key: string;
	gr_user: JSX.Element;
	audio?: boolean;
	video?: boolean;
	email?: string;
}

export default function DebugVideo() {
	const [refresh, setRefresh]: [number, React.Dispatch<React.SetStateAction<number>>] = useState(1);
	const [pendingRegion, setPendingRegion] = useState<{ label: string; value: string } | null>(null);
	const [switching, setSwitching] = useState(false);

	const { opentok } = useContext(OpenTokContext);
	const { getAllAV } = useContext(AVContext);

	const connectedUsers = useSelector((s: GRS) => s.SessionReducer.connectedUsers);
	const clientWebrtcID = useSelector((s: GRS) => s.SessionReducer.webrtcID);
	const clientUser = useSelector((s: GRS) => s.SessionReducer.user);
	const connectedPresenterVideos = useSelector((s: GRS) => s.VideoReducer.connectedPresenterVideos);
	const token = useSelector((s: GRS) => s.SessionReducer.token);
	const meeting = useSelector((s: GRS) => s.SessionReducer.meeting);
	const mediaRegion = useSelector((s: GRS) => s.OpenTokReducer.mediaRegion);
	const roomState = useSelector((s: GRS) => s.MeetingReducer.roomState);

	const [streamBandwidth, setStreamBandwidth] = useState<Map<string, number>>(new Map());
	const prevStatsRef = useRef<Map<string, { bytes: number; ts: number }>>(new Map());

	useEffect(() => {
		if (roomState === ROOM_STATES.SHOWTIME) setPendingRegion(null);
	}, [roomState]);

	const opentokRef = useRef(opentok);
	opentokRef.current = opentok;

	useEffect(() => {
		const pollStats = () => {
			const ot = opentokRef.current;
			if (!ot) return;

			Object.entries(ot.subscribers).forEach(([streamId, sub]) => {
				sub.getStats((err, stats) => {
					if (err || !stats) return;

					const videoBytes = stats.video?.bytesReceived ?? 0;
					const audioBytes = stats.audio?.bytesReceived ?? 0;
					const bytes = videoBytes + audioBytes;
					const { timestamp: ts } = stats;
					const prev = prevStatsRef.current.get(streamId);

					if (prev && ts > prev.ts) {
						const deltaSec = (ts - prev.ts) / 1000;
						const rate = (bytes - prev.bytes) / deltaSec / (1024 * 1024);
						setStreamBandwidth((current) => {
							const updated = new Map(current);
							updated.set(streamId, rate);
							return updated;
						});
					}

					prevStatsRef.current.set(streamId, { bytes, ts });
				});
			});
		};

		pollStats();
		const interval = setInterval(pollStats, 2000);
		return () => clearInterval(interval);
	}, []);

	const handleConfirmRegionSwitch = async () => {
		if (!pendingRegion || !token || !meeting) return;
		setSwitching(true);
		try {
			await SetMediaRegionForRoom(token, meeting.meeting, pendingRegion.value);
			Alerts.showAlert({ message: 'Switching region...', description: `Reconnecting to ${pendingRegion.label}`, type: 'info', duration: 4000 });
		} catch {
			Alerts.showAlert({ message: 'Region switch failed', type: 'error', duration: 4000 });
		} finally {
			setSwitching(false);
			setPendingRegion(null);
		}
	};

	const streams = useMemo(
		(): string[] => Array.from(opentok?.streams.keys() ?? []),
		[opentok?.streams, refresh],
	);

	const mypubs = useMemo(
		(): any [] => opentok?.myPublishers?.map((p) => p.stream?.streamId ?? 'no id') ?? [],
		[opentok?.streams, refresh],
	);

	const displayData: DisplayRow[] = useMemo(() => {
		Object.keys(connectedUsers);
		const avObject = Object.keys(getAllAV());

		const newDisplayData: DisplayRow[] = [];
		avObject.forEach((key) => {
			const matcher = (user: ConnectedUser) => user.webrtcID === key;
			const userObj = connectedUsers.find(matcher);
			if (key === 'local') {
				const newRow = {
					key,
					audio: getAllAV()[key].audio,
					video: getAllAV()[key].video,
					email: `${clientUser?.email ?? 'no email'}`,
					gr_user: <span style={{ color: 'rgb(43, 255, 248)' }}>local ({clientUser?.gr_user ?? 'missing id'})</span>,
				};
				newDisplayData.push(newRow);
			} else {
				const newRow = {
					key,
					audio: getAllAV()[key].audio,
					video: getAllAV()[key].video,
					email: userObj?.email ?? '',
					gr_user: userObj?.gr_user
						? <span style={{ color: 'rgb(43, 255, 248)' }}>{userObj?.gr_user}</span>
						: <><span style={{ color: 'rgb(255, 3, 130)' }}>None</span>, no ConnectedUser found</>,
				};
				newDisplayData.push(newRow);
			}
		});

		return newDisplayData;
	}, [connectedUsers, refresh, getAllAV, refresh]);

	return (
		<div className="section memory-dashboard">
			<h5>AV & ConnectedUser Dataset</h5>
			{displayData.length === 0 && <div>No AV Data</div>}
			{displayData.map((row, index) => (
				<div key={row.key} style={{ padding: '4px 0', lineHeight: 1.3 }}>
					<div>{index + 1}. <span>user {row.gr_user} {row.email}</span>
					<div style={{ paddingBottom: 3, display: 'flex', alignContent: 'center' }}>
						<Icon
							name={row.video ? ICONS.CAMERA_ON : ICONS.CAMERA_OFF}
							color={row.video ? COLORS.WHITE : COLORS.RED}
							size={16}
						/>
						&nbsp;
						<Icon
							name={row.audio ? ICONS.MICROPHONE_ON : ICONS.MICROPHONE_OFF}
							color={row.audio ? COLORS.WHITE : COLORS.RED}
							size={16}
						/>
						Key:&nbsp;<span style={{ color: '#B9E2B6' }}>{row.key}</span></div>
					</div>
				</div>
			))}

			<h5>Connected Presenter Videos </h5>
			{connectedPresenterVideos.map((vid) => {
				const isPaused = opentok?.pausedSubscribers?.has(vid.id) ?? false;
				const subscriber = opentok?.subscribers[vid.id];
				const pubDims = subscriber?.stream?.videoDimensions;
				const recvW = subscriber?.videoWidth();
				const recvH = subscriber?.videoHeight();
				const mbps = streamBandwidth.get(vid.id);
				return (
					<div key={vid.id} style={{ padding: '4px 0', lineHeight: 1.3, borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
						<div>User: <span style={{ color: 'rgb(43, 255, 248)' }}>{vid.profile ?? vid.brandliveProfileId}</span></div>
						<div>Video: {vid.id}</div>
						<div>Stream: <span style={{ color: isPaused ? '#FF6B6B' : '#7CFC00' }}>{isPaused ? 'Paused' : 'Active'}</span></div>
						<div>Recv: {recvW && recvH ? `${recvW}x${recvH}` : '—'} | Pub: {pubDims ? `${pubDims.width}x${pubDims.height}` : '—'}</div>
						<div>Bandwidth: <span style={{ color: '#FFD700' }}>{mbps !== undefined ? `${mbps.toFixed(2)} MB/s` : '—'}</span></div>
						{(vid.isScreenShare || vid.isSipCall || vid.isMobile) && (
							<div>Type: {[vid.isScreenShare && 'screenshare', vid.isSipCall && 'sip', vid.isMobile && 'mobile'].filter(Boolean).join(', ')}</div>
						)}
					</div>
				);
			})}

			<h5>Webrtc ids </h5>
			<div className='padding-bottom-small'>
				SessionReducer.webrtcID (redux)
				<div style={{ color: '#B9E2B6' }}>
					{clientWebrtcID ?? 'null'}
				</div>
			</div>
			<div className='padding-bottom-small'>
				OTClass.publisher.streamId
				<div style={{ color: '#B9E2B6' }}>
					{opentok?.publisher?.streamId ?? 'null'}
				</div>
			</div>

			<div className='padding-bottom-small'>
				OTClass.myPublishers.stream.streamId
				<div style={{ color: '#B9E2B6' }}>
					{mypubs.map((id, i) => <div key={`${id}mypub`}>{i + 1}. {id}</div>)}
				</div>
			</div>

			<div className='padding-bottom-small'>
				OTClass.streams keys
				<div style={{ color: '#B9E2B6' }}>
					{streams.map((webrtcid, i) => <div key={`${webrtcid}webrtcid`}>{i + 1}. {webrtcid}</div>)}
				</div>
			</div>
			<button
				style={{ padding: '8px 12px', marginBottom: '16px', outline: '3px solid #A6FF79' }}
				onClick={() => setRefresh((p) => p + 1)}>
				Trigger dependency arrays for shown data [ {refresh} ]
			</button>
			<div style={{ fontSize: '12px' }}>
				Add <span style={{ color: '#B9E2B6' }}>"?debug=opentok"</span> query string to enable hotkeys
				{/* // see src/utils/video/opentok-connection.ts for more details */}
				<div>[ <span style={{ color: '#B9E2B6' }}>E</span> ] - Failed Connection</div>
				<div>[ <span style={{ color: '#B9E2B6' }}>R</span> ] - Reconnection</div>
			</div>

			&nbsp;
			<h5>Audio Bitrates</h5>
			<div className='padding-bottom-small'>
				{Object.keys(opentok?.stats ?? {}).map((key) => <div key={`${key}key`}><span style={{ color: '#B9E2B6' }}>{key}</span>: {opentok?.stats[key].audioBitrate}</div>)}
			</div>

			&nbsp;

			<h5>Media Region</h5>
			<div className='padding-bottom-small'>
				Current: <span style={{ color: '#B9E2B6' }}>{mediaRegion ?? 'not set'}</span>
			</div>
			{roomState !== ROOM_STATES.SHOWTIME ? (
				<>
					<SelectSmall
						options={VONAGE_REGIONS}
						onChange={(val: string) => {
							const region = VONAGE_REGIONS.find(r => r.value === val);
							if (region) setPendingRegion(region);
						}}
						label="Media Region"
						selected={pendingRegion ?? VONAGE_REGIONS.find(r => r.value === mediaRegion) ?? REGION_PLACEHOLDER}
						dropdown={true}
					/>
					{pendingRegion && (
						<div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
							<span>Switch to &quot;{pendingRegion.label}&quot;?</span>
							<button
								style={{ padding: '4px 10px', outline: '2px solid #A6FF79' }}
								onClick={handleConfirmRegionSwitch}
								disabled={switching}
							>
								{switching ? 'Switching...' : 'Confirm'}
							</button>
							<button
								style={{ padding: '4px 10px', outline: '2px solid #888' }}
								onClick={() => setPendingRegion(null)}
								disabled={switching}
							>
								Cancel
							</button>
						</div>
					)}
				</>
			) : (
				<div style={{ color: '#888', fontSize: '12px' }}>Unavailable during broadcast</div>
			)}

			&nbsp;
		</div>
	);
}
